"use client";

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useLocalStorage, useSessionStorage } from "utils";

interface IReservationProviderProps {
  children: ReactNode;
}

type ReservationContextType = {
  reservationId: string;
  setReservationId: (id: string) => void;
  sidebarOpenedItems: string[];
  storeSidebarOpenedItems: (value: string[]) => void;
  checkOpen: (value: string) => boolean;
  storeMagicAiStatus: (value: string) => void;
  isMagicAiEnabled: () => boolean;
};

const ReservationContext = createContext({} as ReservationContextType);
export function useReservationContext() {
  return useContext(ReservationContext);
}

export function ReservationProvider({ children }: IReservationProviderProps) {
  const [selectedReservationId, setSelectedReservationId] = useSessionStorage(
    "selectedReservation",
    ""
  );
  const [sidebarOpenedItems, setSidebarOpenedItems] = useLocalStorage<string[]>(
    "sidebarOpenedItems",
    []
  );
  const [magicAIEnabled, setMagicAIEnabled] = useSessionStorage("magicAI", "");

  const setReservationId = useCallback((id: string) => {
    setSelectedReservationId(id);
  }, [setSelectedReservationId]);

  const storeSidebarOpenedItems = useCallback((value: string[]) => {
    setSidebarOpenedItems(value);
  }, [setSidebarOpenedItems]);

  const checkOpen = useCallback((value: string) => {
    return sidebarOpenedItems.includes(value);
  }, [sidebarOpenedItems]);

  const storeMagicAiStatus = useCallback((value: string) => {
    setMagicAIEnabled(value);
  }, [setMagicAIEnabled]);

  const isMagicAiEnabled = useCallback(() => {
    return selectedReservationId !== "" && magicAIEnabled !== "";
  }, [selectedReservationId, magicAIEnabled]);

  const contextValue = useMemo(() => ({
    reservationId: selectedReservationId,
    setReservationId,
    sidebarOpenedItems,
    storeSidebarOpenedItems,
    checkOpen,
    storeMagicAiStatus,
    isMagicAiEnabled,
  }), [selectedReservationId, setReservationId, sidebarOpenedItems, storeSidebarOpenedItems, checkOpen, storeMagicAiStatus, isMagicAiEnabled]);

  return (
    <ReservationContext.Provider value={contextValue}>
      {children}
    </ReservationContext.Provider>
  );
}